import { useMemo, useState } from "react"
import { Box, ButtonBase, Stack } from "@mui/material"
import dayjs from "dayjs"
import { ViraCard, ViraText, Heading } from "@ksana/vira-ui"
import {
  ClientFragment,
  Enum_Feature_Direction_Enum,
  Enum_Feature_Type_Enum,
} from "../../generated/graphql"
import { useGetUserInsights } from "../../hooks/useGetUserInsights"
import { getIcon } from "../../utils/getInsightIcon"
import { colors } from "../../utils/theme"
import { TIME_PERIOD, UserInsightsTableData } from "../../utils/types"
import ChevronRightIcon from "../atoms/ChevronRightIcon"
import { UserInsightsDialog } from "./UserInsightsDialog"

type PrepareInsightsProps = {
  client: ClientFragment
  clientFirstName: string
}

const PERIOD_OPTIONS: { value: TIME_PERIOD; label: string }[] = [
  { value: TIME_PERIOD.WEEK, label: "Week" },
  { value: TIME_PERIOD.MONTH, label: "Month" },
]

/** Soft tint behind feature icons — matches Loop Prepare mock. */
function iconWellColor(feature?: Enum_Feature_Type_Enum | null): string {
  switch (feature) {
    case Enum_Feature_Type_Enum.AmtSleepDayMin:
    case Enum_Feature_Type_Enum.ValBedtimeOffsetHour:
    case Enum_Feature_Type_Enum.ValWaketimeOffsetHour:
    case Enum_Feature_Type_Enum.ValMorningness:
      return "#EDE8F8"
    case Enum_Feature_Type_Enum.AmtAutomotiveDayMin:
      return "#E6F4EA"
    case Enum_Feature_Type_Enum.AmtWalkingDayMin:
    case Enum_Feature_Type_Enum.AmtExerciseDayMin:
    case Enum_Feature_Type_Enum.AmtStationaryDayMin:
      return "#E8F1FB"
    case Enum_Feature_Type_Enum.PropGpsDayHome:
      return "#FFF3E0"
    case Enum_Feature_Type_Enum.NCharDay:
    case Enum_Feature_Type_Enum.NWordDay:
    case Enum_Feature_Type_Enum.PropWordAbsDay:
    case Enum_Feature_Type_Enum.PropWordIDay:
    case Enum_Feature_Type_Enum.PropWordNegDay:
    case Enum_Feature_Type_Enum.PropWordPosDay:
      return "#FCE8EF"
    default:
      return "#EEF1F6"
  }
}

/**
 * Action-style titles used in the Loop Prepare mock (and mobile insight art
 * naming). Falls back to combo display_text when unmapped.
 */
function insightCardTitle(
  feature?: Enum_Feature_Type_Enum | null,
  direction?: Enum_Feature_Direction_Enum | null,
  displayText?: string | null
): string {
  const fallback = displayText?.trim() || "Insight"
  if (!feature || !direction) {
    return fallback
  }

  switch (feature) {
    case Enum_Feature_Type_Enum.AmtSleepDayMin:
      if (direction === Enum_Feature_Direction_Enum.More)
        return "Get more sleep"
      if (direction === Enum_Feature_Direction_Enum.Less)
        return "Get less sleep"
      break
    case Enum_Feature_Type_Enum.AmtAutomotiveDayMin:
      if (direction === Enum_Feature_Direction_Enum.More) return "Drive more"
      if (direction === Enum_Feature_Direction_Enum.Less) return "Drive less"
      break
    case Enum_Feature_Type_Enum.AmtExerciseDayMin:
    case Enum_Feature_Type_Enum.AmtWalkingDayMin:
      if (direction === Enum_Feature_Direction_Enum.More) return "Move more"
      if (direction === Enum_Feature_Direction_Enum.Less) return "Move less"
      break
    case Enum_Feature_Type_Enum.AmtStationaryDayMin:
      if (direction === Enum_Feature_Direction_Enum.More)
        return "Spend more time stationary"
      if (direction === Enum_Feature_Direction_Enum.Less)
        return "Spend less time stationary"
      break
    case Enum_Feature_Type_Enum.PropGpsDayHome:
      if (direction === Enum_Feature_Direction_Enum.More)
        return "Spend more time at home"
      if (direction === Enum_Feature_Direction_Enum.Less)
        return "Spend less time at home"
      break
    default:
      break
  }
  return fallback
}

/** Clinician-facing copy: swap second-person “you/your” for the client’s name. */
function clinicianObserveText(
  text: string | null | undefined,
  firstName: string
): string {
  const raw = (text || "").trim()
  if (!raw) {
    return ""
  }
  if (!firstName) {
    return raw
  }
  const possessive = `${firstName}'s`
  return raw
    .replace(/\bYour\b/g, possessive)
    .replace(/\byour\b/g, possessive)
    .replace(/\bYou\b/g, firstName)
    .replace(/\byou\b/g, firstName)
}

function isInsightVisible(
  row: UserInsightsTableData,
  period: TIME_PERIOD
): boolean {
  const feature = row.insight.data?.insight_combo?.feature
  if (!feature || feature === Enum_Feature_Type_Enum.InvalidNotEnoughData) {
    return false
  }
  const created = dayjs(row.date.data)
  if (!created.isValid()) {
    return false
  }
  if (period === TIME_PERIOD.WEEK) {
    return created.isAfter(dayjs().startOf("day").subtract(7, "day"))
  }
  if (period === TIME_PERIOD.MONTH) {
    return created.isAfter(dayjs().startOf("day").subtract(30, "day"))
  }
  return true
}

/**
 * Loop Prepare “Latest Insights” block — matches the FDD mock (personalized
 * title, Week/Month toggle, icon rows → existing insight details modal).
 */
export default function PrepareInsights({
  client,
  clientFirstName,
}: PrepareInsightsProps) {
  const allInsights = useGetUserInsights(client.id)
  const [period, setPeriod] = useState<TIME_PERIOD>(TIME_PERIOD.WEEK)
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(0)

  const rows = useMemo(
    () => allInsights.filter((row) => isInsightVisible(row, period)),
    [allInsights, period]
  )

  const titleName = clientFirstName.trim() || "this client"
  const rowBorder = "1px solid rgba(200, 205, 219, 0.85)"

  return (
    <>
      <ViraCard
        padded
        sx={{
          width: "100%",
          // Soft well so nested insight rows read like the FDD mock cards.
          backgroundColor: colors.neutral.white,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Box sx={{ minWidth: 0, flex: 1, pr: { sm: 2 } }}>
            <Heading
              level="card"
              sx={{ mb: 0.75, fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}
            >
              {`What ${titleName}'s best days have in common`}
            </Heading>
            <ViraText
              tone="muted"
              sx={{ fontSize: 14, lineHeight: 1.5, maxWidth: 520 }}
            >
              Insights from phone data and daily enjoyment check-ins. The client
              sees these too
            </ViraText>
          </Box>
          <Box
            role="group"
            aria-label="Insight time period"
            sx={{
              display: "inline-flex",
              alignSelf: { xs: "stretch", sm: "flex-start" },
              flexShrink: 0,
              mt: { sm: 0.25 },
              p: "2px",
              borderRadius: "8px",
              border: rowBorder,
              backgroundColor: colors.neutral.white,
            }}
          >
            {PERIOD_OPTIONS.map((option) => {
              const selected = option.value === period
              return (
                <ButtonBase
                  key={option.value}
                  aria-pressed={selected}
                  onClick={() => setPeriod(option.value)}
                  sx={{
                    flex: { xs: "1 1 0", sm: "0 0 auto" },
                    minWidth: 58,
                    px: 1.5,
                    py: 0.65,
                    borderRadius: "6px",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: selected
                      ? colors.neutral.white
                      : colors.neutral.denim,
                    backgroundColor: selected
                      ? colors.neutral.denim
                      : "transparent",
                    "&:hover": {
                      backgroundColor: selected
                        ? colors.neutral.denim
                        : "rgba(47, 59, 90, 0.06)",
                    },
                  }}
                >
                  {option.label}
                </ButtonBase>
              )
            })}
          </Box>
        </Stack>

        <Stack spacing={1.5} sx={{ mt: 2.5 }}>
          {rows.length === 0 ? (
            <ViraText tone="muted" sx={{ fontSize: 14, py: 1 }}>
              No insights in this period yet.
            </ViraText>
          ) : (
            rows.map((row) => {
              const combo = row.insight.data?.insight_combo
              const recipe = row.insight.data?.insightRecipe
              const feature = combo?.feature
              // Prefer MVP2 badge icons — closer to the colored glyph wells in
              // Danielle’s mock than the legacy outline SVGs.
              const image = getIcon(feature, true)
              const heading = insightCardTitle(
                feature,
                combo?.direction,
                combo?.display_text ?? row.display_text.data
              )
              const body = clinicianObserveText(
                recipe?.observeText?.content ?? combo?.observe_text,
                titleName
              )

              return (
                <ButtonBase
                  key={String(row.id.data)}
                  onClick={() => {
                    if (!recipe?.id) {
                      return
                    }
                    setSelectedId(recipe.id)
                    setOpen(true)
                  }}
                  disabled={!recipe?.id}
                  sx={{
                    display: "flex",
                    width: "100%",
                    textAlign: "left",
                    alignItems: "center",
                    gap: 1.75,
                    px: 1.75,
                    py: 1.6,
                    borderRadius: "12px",
                    border: rowBorder,
                    backgroundColor: colors.neutral.white,
                    boxShadow: "0 1px 0 rgba(30, 38, 60, 0.02)",
                    transition: "background-color 120ms ease",
                    "&:hover": {
                      backgroundColor: "rgba(47, 59, 90, 0.03)",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.55,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: iconWellColor(feature),
                    }}
                  >
                    {image ? (
                      <Box
                        component="img"
                        src={image}
                        alt=""
                        sx={{ width: 26, height: 26 }}
                      />
                    ) : null}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ViraText
                      sx={{
                        display: "block",
                        fontSize: 15.5,
                        fontWeight: 700,
                        color: colors.neutral.denim,
                        lineHeight: 1.25,
                        mb: body ? 0.4 : 0,
                      }}
                    >
                      {heading}
                    </ViraText>
                    {body ? (
                      <ViraText
                        tone="muted"
                        sx={{
                          display: "block",
                          fontSize: 14,
                          lineHeight: 1.45,
                        }}
                      >
                        {body}
                      </ViraText>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: "flex",
                      opacity: 0.45,
                      "& svg": { width: 20, height: 20 },
                    }}
                  >
                    <ChevronRightIcon />
                  </Box>
                </ButtonBase>
              )
            })
          )}
        </Stack>
      </ViraCard>

      <UserInsightsDialog
        insightId={selectedId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  )
}
